import type { ReactNode } from 'react';

import logoWhite from '@/assets/brand/logo-white.png';

interface AuthShellProps {
  children: ReactNode;
  /** Right-side decorative pane (typically <MarketingCarousel />). Visible only at lg+. */
  marketing?: ReactNode;
  /** Optional sticky bottom bar (e.g. Back/Next on onboarding pages). When
   *  present, the form area lays out as a vertical column with the footer
   *  pinned to the viewport bottom; the form scrolls between the sticky logo
   *  header and the sticky footer. When omitted, children are centered
   *  vertically (login/signup/verify/forgot pattern). */
  footer?: ReactNode;
}

// Two-pane layout shared by login, signup, personal-detail, and workflow-setup.
// The form pane is 2/3 width at lg+, full width below.
// The marketing pane is 1/3 at lg+, hidden below.
//
// Full-bleed height: h-dvh (dynamic viewport, ignores iOS Safari's collapsing
// URL bar). On big screens the form has all the room it needs AND Back/Next
// remain visible at the bottom -- the footer is sticky-bottom inside the
// scrolling form pane, so it stays put whether the form fits or scrolls.
export function AuthShell({ children, marketing, footer }: AuthShellProps) {
  return (
    <div className="auth-page h-dvh overflow-hidden">
      <div className="h-full grid lg:grid-cols-3">
        <div className="bg-white overflow-y-auto hide-scrollbar w-full flex flex-col tracking-tight lg:col-span-2">
          {/* Sticky logo bar -- the `::after` paints a white safe area above
              it so content scrolling past the logo gets covered cleanly. */}
          {/* Sticky logo — after: white safe area above so scrolled content is covered */}
          <div className="px-5 sm:px-8 lg:px-20 pt-5 sm:pt-8 lg:pt-8 sticky top-0 z-10 bg-white after:absolute after:content-[''] after:bottom-full after:start-0 after:bg-white after:w-full after:h-5 sm:after:h-8 lg:after:h-8 after:pointer-events-none">
            <div className="max-h-10 lg:max-h-13 h-full">
              <img
                src={logoWhite}
                alt="SignCare"
                className="object-contain h-full max-lg:mx-auto"
              />
            </div>
          </div>
          {footer ? (
            <>
              <div className="flex flex-col items-center flex-1 px-5 sm:px-8 lg:px-20 pt-5 sm:pt-8 lg:pt-8 pb-3">
                {children}
              </div>
              {/* Sticky footer — after: white safe area below so scrolled content is covered */}
              <div className="sticky bottom-0 z-10 bg-white px-5 sm:px-8 lg:px-20 py-3 sm:py-4 lg:py-4 after:absolute after:content-[''] after:top-full after:start-0 after:bg-white after:w-full after:h-5 sm:after:h-8 lg:after:h-8 after:pointer-events-none">
                {footer}
              </div>
            </>
          ) : (
            // No footer -> centered form column (login/signup/verify/forgot).
            <div className="flex items-center justify-center flex-1 px-5 sm:px-8 lg:px-20 pb-5 sm:pb-8 lg:pb-20 pt-5 sm:pt-8 lg:pt-10">
              {children}
            </div>
          )}
        </div>
        {marketing}
      </div>
    </div>
  );
}
